import { IoClose } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Universal Modal — used across ALL pages.
 *
 * Props:
 *   isOpen       {boolean}   — controls visibility
 *   onClose      {function}  — called when overlay or X is clicked
 *   name         {string}    — title shown in the header
 *   widthClass   {string}    — Tailwind max-width class (default: "max-w-3xl")
 *   children     {ReactNode} — body content
 */
const Model = ({
  isOpen,
  onClose,
  children,
  name,
  widthClass = "max-w-3xl",
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center font-plus">
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-[2px]"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            key="panel"
            initial={{ opacity: 0, scale: 0.97, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            className={`relative w-full ${widthClass} max-h-[92vh] bg-white flex flex-col rounded-none shadow-[0_24px_60px_rgba(15,23,42,0.18)] border border-slate-200`}
          >
            {/* ── Header ── */}
            <div className="shrink-0 flex items-center justify-between px-7 py-4 border-b border-slate-100 bg-white">
              <div className="flex flex-col gap-0.5">
                <h2 className="text-[13px] font-black text-slate-800 uppercase tracking-[2.5px] leading-none">
                  {name}
                </h2>
                <div className="w-8 h-[3px] bg-primary mt-1.5" />
              </div>
              <button
                onClick={onClose}
                className="p-1.5 text-slate-400 hover:text-slate-700 border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all"
                aria-label="Close"
              >
                <IoClose size={18} />
              </button>
            </div>

            {/* ── Body ── */}
            <div className="flex-1 overflow-y-auto bg-slate-50/50 custom-scrollbar">
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default Model;
